/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { InputProps } from './types';
import BasicIcon from '../BasicIcon/BasicIcon';
import {
  Container,
  Icon,
  InputContainer,
  Input,
  Placeholder,
  Label,
  FixedLabel,
  HelperMessage,
  ErrorMessage,
  HelperContainer,
  AddOnContainer,
  BoxContainer,
} from './styles';

const InputComponent: React.FC<InputProps> = (props) => {
  const {
    id,
    input,
    meta,
    value,
    placeholder,
    label,
    type = 'text',
    readOnly,
    disabled,
    testId,
    size,
    variant = 'underline',
    typingAllowed = true,
    isOpen,
    showClearIcon = true,
    helperText,
    pointer,
    leftAddOn,
    rightAddOn,
    inputRef,
    maxLength,
    onChange,
    onClear,
    onBlur,
    onFocus,
    onMouseDown,
    onKeyDown,
    ellipsis,
  } = props;
  const { actualTheme, isMobile } = useContext(ThemeContext);
  const [isFocus, setIsFocus] = useState(false);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const inputValue = useMemo(() => value ?? input.value, [input.value, value]);
  const hasIcon = useMemo(() => (showClearIcon && inputValue) || rightAddOn, [
    rightAddOn,
    inputValue,
    showClearIcon,
  ]);
  const inputSize = useMemo(() => {
    if (!size && isMobile) return 'small';
    return size || 'standard';
  }, [size, isMobile]);

  const handleClear = useCallback(() => {
    if (!disabled && !readOnly) {
      if (onClear) onClear();
      if (input?.onBlur) input.onBlur();
    }
  }, [input, onClear, disabled, readOnly]);
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange && !disabled && typingAllowed && onChange(event.target.value),
    [onChange, disabled, typingAllowed],
  );
  const handleBlur = useCallback(() => {
    setIsFocus(false);
    if (!disabled && !readOnly) {
      if (onBlur) onBlur();
      if (input?.onBlur) input.onBlur();
    }
  }, [input, onBlur, disabled, readOnly, setIsFocus]);
  const handleFocus = useCallback(() => {
    setIsFocus(true);
    if (!disabled && !readOnly) {
      if (onFocus) onFocus();
      if (input?.onFocus) input.onFocus();
    }
  }, [input, onFocus, disabled, readOnly, setIsFocus]);
  const handleMouseDown = useCallback(() => {
    if (onMouseDown && !disabled && !readOnly) onMouseDown();
  }, [onMouseDown, disabled, readOnly]);

  const renderClearIcon = useCallback(() => {
    if (inputValue && showClearIcon) {
      const { grayB8, gray80, status } = actualTheme;
      return (
        <Icon
          disabled={disabled || readOnly}
          onClick={handleClear}
          data-testid={`${testId}-clear-icon`}
        >
          <BasicIcon
            size="medium"
            name="x"
            color={hasError ? status.error.color : disabled ? grayB8 : gray80}
          />
        </Icon>
      );
    }
    return null;
  }, [inputValue, showClearIcon, actualTheme, disabled, readOnly, handleClear, testId, hasError]);

  const showRightAddOn = useCallback(() => rightAddOn || null, [rightAddOn]);

  const showLeftAddOn = useCallback(() => {
    if (leftAddOn) {
      return (
        <AddOnContainer
          size={inputSize}
          hasError={hasError}
          disabled={disabled}
          readOnly={readOnly}
        >
          {leftAddOn}
        </AddOnContainer>
      );
    }

    return null;
  }, [disabled, hasError, leftAddOn, inputSize, readOnly]);

  const showHelperOrError = useCallback(() => {
    if (hasError) {
      return (
        <>
          <ErrorMessage data-testid={`${testId}-error`}>{meta.error}</ErrorMessage>
        </>
      );
    }

    if (helperText)
      return (
        <HelperMessage disabled={disabled} data-testid={`${testId}-help-message`}>
          {helperText}
        </HelperMessage>
      );

    return null;
  }, [hasError, helperText, disabled, testId, meta.error]);

  return (
    <ThemeProvider theme={actualTheme}>
      <Container size={inputSize || 'standard'} variant={variant}>
        {label && variant === 'top-aligned-label' && (
          <FixedLabel
            hasError={hasError}
            size={inputSize || 'standard'}
            disabled={disabled}
            data-testid={`${testId}-label`}
          >
            {label}
          </FixedLabel>
        )}
        <BoxContainer
          id={id}
          variant={variant}
          hasError={hasError}
          size={inputSize || 'standard'}
          isOpen={isOpen}
          disabled={disabled}
          hasPlaceholder={!!placeholder}
          isFocus={isFocus}
        >
          {showLeftAddOn()}
          <InputContainer id="text-input-id">
            <Input
              {...input}
              readOnly={!typingAllowed || readOnly}
              disabled={disabled}
              value={inputValue}
              type={type}
              data-testid={testId}
              autoComplete="off"
              hasPlaceholder={!!placeholder}
              inputSize={inputSize || 'standard'}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              pointer={pointer}
              onMouseDown={handleMouseDown}
              isOpen={isOpen}
              ref={inputRef}
              variant={variant}
              onKeyDown={onKeyDown}
              maxLength={maxLength}
              ellipsis={ellipsis}
            />
            {variant !== 'top-aligned-label' && (
              <Label
                hasError={hasError}
                size={inputSize || 'standard'}
                disabled={disabled}
                data-testid={`${testId}-label`}
              >
                {label}
              </Label>
            )}
            <Placeholder
              hide={inputValue}
              hasError={hasError}
              size={inputSize || 'standard'}
              data-testid={`${testId}-placeholder`}
            >
              {placeholder}
            </Placeholder>
          </InputContainer>
          {hasIcon && (
            <AddOnContainer
              size={inputSize}
              hasError={hasError}
              disabled={disabled}
              readOnly={readOnly}
            >
              {showRightAddOn()}
              {renderClearIcon()}
            </AddOnContainer>
          )}
        </BoxContainer>
        <HelperContainer>{showHelperOrError()}</HelperContainer>
      </Container>
    </ThemeProvider>
  );
};

export default InputComponent;
